'use client';

import { useRef, useState } from 'react';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';

import LogoShort from '@/components/SvgIcons/LogoShort';
import { type NavLinkWithChildren } from '@/config/navigation.config';
import { Link } from '@/i18n/navigation';
import useToggleState from '@/utils/hooks/useToggleState';

import Actions from './Actions';
import BlurOverlay from './BlurOverlay';
import styles from './Header.module.scss';
import MenuDrawer from './MenuDrawer';
import Navigation from './Navigation';

const Header = () => {
  const [mobileMenuOpen, toggleMobileMenu] = useToggleState();
  const [openLink, setOpenLink] = useState<NavLinkWithChildren | null>(null);
  const [isOverlayClosing, setIsOverlayClosing] = useState(false);
  const [isPanelClosing, setIsPanelClosing] = useState(false);
  const [closingLink, setClosingLink] = useState<NavLinkWithChildren | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleOpen = (link: NavLinkWithChildren) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setOpenLink(link);
  };

  const handleClose = () => {
    closeTimerRef.current = setTimeout(() => {
      setClosingLink(openLink);
      setIsPanelClosing(true);
      setIsOverlayClosing(true);
      setOpenLink(null);
      closeTimerRef.current = null;
    }, 150);
  };

  const handleCloseImmediate = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setClosingLink(openLink);
    setIsPanelClosing(true);
    setIsOverlayClosing(true);
    setOpenLink(null);
  };

  const handlePanelCloseComplete = () => {
    setClosingLink(null);
    setIsPanelClosing(false);
  };

  const handlePanelMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handlePanelMouseLeave = () => {
    handleClose();
  };

  return (
    <>
      <AppBar position="fixed" classes={{ root: styles.root }} className={styles.container} elevation={0}>
        <Toolbar className={styles.toolbar}>
          <Stack direction="row" spacing={2} alignItems="center" flex={1}>
            <Link href="/" aria-label="Workspace - Home" className={styles.logo}>
              <LogoShort />
            </Link>
            <Box className={styles.navWrapper}>
              <Navigation
                openLink={openLink}
                panelLink={openLink || closingLink}
                isPanelOpen={!!openLink}
                isPanelClosing={isPanelClosing}
                onOpen={handleOpen}
                onClose={handleClose}
                onPanelMouseEnter={handlePanelMouseEnter}
                onPanelMouseLeave={handlePanelMouseLeave}
                onCloseImmediate={handleCloseImmediate}
                onPanelCloseComplete={handlePanelCloseComplete}
              />
            </Box>
          </Stack>
          <Actions menuOpen={mobileMenuOpen} onMenuToggle={toggleMobileMenu} />
        </Toolbar>
      </AppBar>
      <MenuDrawer open={mobileMenuOpen} onClose={toggleMobileMenu} />
      {(openLink || isOverlayClosing) && (
        <BlurOverlay
          open={!!openLink}
          onClose={handleCloseImmediate}
          onCloseComplete={() => setIsOverlayClosing(false)}
        />
      )}
    </>
  );
};

export default Header;
